import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'tracker-ready-month-separator-display';

export interface ReadyMonthSeparatorDisplay {
  /** «Всего часов» по готовым за месяц */
  showReadyTime: boolean;
  /** Часы «в процессе» на разделителе (видны только у самого нового месяца) */
  showInProgress: boolean;
  /** «Готовых проектов: N» */
  showProjectCount: boolean;
  /** «Проектов в процессе: N» (видно только у самого нового месяца) */
  showActiveProjectsCount: boolean;
}

const DEFAULT_DISPLAY: ReadyMonthSeparatorDisplay = {
  showReadyTime: true,
  showInProgress: true,
  showProjectCount: true,
  showActiveProjectsCount: true,
};

function loadFromStorage(): ReadyMonthSeparatorDisplay {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DISPLAY };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      showReadyTime:
        typeof parsed.showReadyTime === 'boolean' ? parsed.showReadyTime : DEFAULT_DISPLAY.showReadyTime,
      showInProgress:
        typeof parsed.showInProgress === 'boolean' ? parsed.showInProgress : DEFAULT_DISPLAY.showInProgress,
      showProjectCount:
        typeof parsed.showProjectCount === 'boolean'
          ? parsed.showProjectCount
          : DEFAULT_DISPLAY.showProjectCount,
      showActiveProjectsCount:
        typeof parsed.showActiveProjectsCount === 'boolean'
          ? parsed.showActiveProjectsCount
          : DEFAULT_DISPLAY.showActiveProjectsCount,
    };
  } catch {
    return { ...DEFAULT_DISPLAY };
  }
}

function saveToStorage(value: ReadyMonthSeparatorDisplay): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export function useReadyMonthSeparatorDisplay(): {
  monthSepDisplay: ReadyMonthSeparatorDisplay;
  setMonthSepShowReadyTime: (value: boolean) => void;
  setMonthSepShowInProgress: (value: boolean) => void;
  setMonthSepShowProjectCount: (value: boolean) => void;
  setMonthSepShowActiveProjectsCount: (value: boolean) => void;
} {
  const [monthSepDisplay, setMonthSepDisplay] = useState<ReadyMonthSeparatorDisplay>(loadFromStorage);

  useEffect(() => {
    saveToStorage(monthSepDisplay);
  }, [monthSepDisplay]);

  const setMonthSepShowReadyTime = useCallback((value: boolean) => {
    setMonthSepDisplay((prev) => ({ ...prev, showReadyTime: value }));
  }, []);

  const setMonthSepShowInProgress = useCallback((value: boolean) => {
    setMonthSepDisplay((prev) => ({ ...prev, showInProgress: value }));
  }, []);

  const setMonthSepShowProjectCount = useCallback((value: boolean) => {
    setMonthSepDisplay((prev) => ({ ...prev, showProjectCount: value }));
  }, []);

  const setMonthSepShowActiveProjectsCount = useCallback((value: boolean) => {
    setMonthSepDisplay((prev) => ({ ...prev, showActiveProjectsCount: value }));
  }, []);

  return {
    monthSepDisplay,
    setMonthSepShowReadyTime,
    setMonthSepShowInProgress,
    setMonthSepShowProjectCount,
    setMonthSepShowActiveProjectsCount,
  };
}
